import React, { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, ExternalLink, Lock, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CATEGORY_META, PLANNING_CATEGORIES, TEACHING_CATEGORIES, resourceUrl } from './resourceLibraryApi';
import { PdfViewer } from './PdfViewer';

const ORDER = [...PLANNING_CATEGORIES, ...TEACHING_CATEGORIES];
const isPdf = (r) => (r.mime_type || '').includes('pdf') || /\.pdf$/i.test(r.file_name || '');

export const ResourceViewerDialog = ({ resources, initialId, token, onClose }) => {
  const ordered = useMemo(
    () => [...resources].sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category)),
    [resources],
  );
  const [currentId, setCurrentId] = useState(initialId);
  useEffect(() => setCurrentId(initialId), [initialId]);

  const idx = ordered.findIndex((r) => r.id === currentId);
  const current = ordered[idx];
  if (!current) return null;

  const planning = ordered.filter((r) => PLANNING_CATEGORIES.includes(r.category));
  const teaching = ordered.filter((r) => TEACHING_CATEGORIES.includes(r.category));
  const go = (d) => setCurrentId(ordered[Math.min(ordered.length - 1, Math.max(0, idx + d))].id);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="resource-viewer">
        <div className="flex items-center gap-3 px-4 py-2 border-b bg-slate-50">
          <Badge variant="outline" className={CATEGORY_META[current.category]?.color}>{current.category_label}</Badge>
          <DialogTitle className="text-base font-semibold text-slate-900 truncate flex-1" data-testid="resource-viewer-title">{current.title}</DialogTitle>
          <Button size="sm" variant="outline" onClick={() => go(-1)} disabled={idx === 0} data-testid="resource-viewer-prev"><ChevronLeft className="h-4 w-4" />Previous</Button>
          <Button size="sm" variant="outline" onClick={() => go(1)} disabled={idx === ordered.length - 1} data-testid="resource-viewer-next">Next<ChevronRight className="h-4 w-4" /></Button>
          <Button asChild size="sm" variant="ghost"><a href={resourceUrl(current, token)} target="_blank" rel="noreferrer" title="Open in new tab"><ExternalLink className="h-4 w-4" /></a></Button>
          <Button asChild size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-8"><a href={resourceUrl(current, token, true)} download={current.file_name}><Download className="h-4 w-4 mr-1" />Download</a></Button>
        </div>
        <div className="flex flex-1 min-h-0">
          <aside className="w-64 shrink-0 border-r bg-white overflow-y-auto p-3 space-y-4 hidden md:block" data-testid="resource-viewer-nav">
            {planning.length > 0 && (
              <NavGroup title="Plan the lesson" items={planning} currentId={currentId} onPick={setCurrentId} numbered />
            )}
            {teaching.length > 0 && (
              <NavGroup title="Teach the lesson" items={teaching} currentId={currentId} onPick={setCurrentId} />
            )}
          </aside>
          <div className="flex-1 min-w-0 min-h-0">
            {isPdf(current) ? (
              <PdfViewer key={current.id} url={resourceUrl(current, token)} />
            ) : (
              <iframe key={current.id} title={current.title} src={resourceUrl(current, token)} className="w-full h-full bg-white" data-testid="resource-viewer-frame" />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function NavGroup({ title, items, currentId, onPick, numbered }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{title}</p>
      <div className="space-y-1">
        {items.map((r, i) => {
          const active = r.id === currentId;
          return (
            <button key={r.id} type="button" onClick={() => onPick(r.id)} data-testid={`resource-viewer-nav-${r.id}`}
              className={`w-full text-left rounded-md px-2.5 py-2 text-sm flex items-start gap-2 transition-colors ${active ? 'bg-emerald-600 text-white' : 'hover:bg-slate-100 text-slate-700'}`}>
              {numbered ? (
                <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-full text-[11px] font-bold flex items-center justify-center ${active ? 'bg-white text-emerald-700' : 'bg-amber-500 text-white'}`}>{i + 1}</span>
              ) : (
                <FileText className={`h-4 w-4 mt-0.5 shrink-0 ${active ? 'text-white' : 'text-slate-400'}`} />
              )}
              <span className="min-w-0">
                <span className="block font-medium leading-tight">{r.category_label}</span>
                <span className={`block text-xs truncate ${active ? 'text-emerald-50' : 'text-slate-500'}`}>{r.title}</span>
                {r.visibility === 'staff' && !active && <Lock className="inline h-3 w-3 text-rose-400 mt-1" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
